import { Box, VStack, Drawer, Portal, Button, Icon } from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import React from 'react';
import PropertySelectorGroupMobile from './PropertySelectorGroupMobile.tsx';
import PropertySelectorSearch from './PropertySelectorSearch.tsx';
import { usePropertySelectorModel } from './usePropertySelectorModel.ts';
import SavedCardPaymentConfirmationDialog from '@/components/payment/SavedCardPaymentConfirmationDialog.tsx';
import { CloseButton } from '@/components/ui/close-button.tsx';
import { ROUNDED_FOCUS_RING } from '@/components/ui/focusStyles.ts';

interface PropertySelectorMobileProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	unreadNotificationPropertyIds: Set<number>;
}

/**
 * Mobile-only property selector component displaying properties in groups
 * Each group shows properties as 2-row cards with icon and title
 * Similar structure to PropertySelectorDesktop but with mobile card layout
 */
const PropertySelectorMobile: React.FC<PropertySelectorMobileProps> = (props) => {
	const { open, onOpenChange, unreadNotificationPropertyIds } = props;
	const {
		groups,
		selectedHouseId,
		handleHouseSelect,
		startSubscribeCheckout,
		isSubscribeCheckingOut,
		isSubscribeConfirmationOpen,
		subscribeSavedCard,
		subscribePendingAmountCents,
		confirmSubscribeCheckout,
		cancelSubscribeCheckout,
		isHouseLocked,
		transferringPropertyIds,
		totalProperties,
		searchQuery,
		setSearchQuery,
		showSearch,
		canAddNewProperties,
		startAddProperty,
		confirmCheckout,
		cancelCheckout,
		isConfirmationOpen,
		savedCard,
		pendingPayment,
		isCheckingOut,
		canStartFlow,
	} = usePropertySelectorModel();

	return (
		<>
			<Drawer.Root placement="bottom" open={open} onOpenChange={({ open }) => onOpenChange(open)}>
				<Portal>
					<Drawer.Backdrop />
					<Drawer.Positioner>
						<Drawer.Content
							borderTopRadius="3xl"
							boxShadow="lg"
							bg="surface.default"
							overflow="hidden"
							// Stop short of the very top so the sheet reads as a sheet (and the status bar /
							// address bar stay clear); the body scrolls within this cap when the list is long.
							maxH="90dvh"
							display="flex"
							flexDirection="column"
						>
							<Drawer.Header
								bg="surface.default"
								borderTopRadius="3xl"
								position="relative"
								zIndex={3}
								flexShrink={0}
							>
								<Box w="full" display="flex" alignItems="center" justifyContent="space-between">
									Select a Property Bundle
									<Drawer.CloseTrigger asChild>
										<CloseButton size="sm" />
									</Drawer.CloseTrigger>
								</Box>
							</Drawer.Header>
							<Drawer.Body
								display="flex"
								flexDirection="column"
								gap={0}
								px={4}
								pt={0}
								pb={4}
								flex="1"
								minH={0}
								overflowY="auto"
							>
								{showSearch && (
									<Box
										position="sticky"
										top={0}
										zIndex={2}
										bg="surface.default"
										mx={-4}
										px={4}
										pt={1}
										pb={4}
									>
										<PropertySelectorSearch
											totalProperties={totalProperties}
											searchQuery={searchQuery}
											onSearchChange={setSearchQuery}
											variant="pill"
										/>
									</Box>
								)}

								<VStack gap={6} w="full" align="start">
									{groups.map((group) => (
										<PropertySelectorGroupMobile
											key={group.key}
											groupKey={group.key}
											title={group.title}
											houses={group.houses}
											lockedCount={group.lockedCount}
											selectedHouseId={selectedHouseId}
											onHouseSelect={handleHouseSelect}
											isHouseLocked={isHouseLocked}
											onSubscribe={startSubscribeCheckout}
											isSubscribeLoading={isSubscribeCheckingOut}
											transferringPropertyIds={transferringPropertyIds}
											unreadNotificationPropertyIds={unreadNotificationPropertyIds}
										/>
									))}
								</VStack>

								{canAddNewProperties && (
									<Box w="full" pt={2}>
										<Button
											onClick={startAddProperty}
											variant="outline"
											borderRadius="full"
											textStyle="body.sm"
											fontWeight="medium"
											size="lg"
											minH="52px"
											color="brand.fg"
											w="full"
											boxShadow="sm"
											loading={isCheckingOut}
											disabled={!canStartFlow}
											_focusVisible={ROUNDED_FOCUS_RING}
										>
											<Icon as={FiPlus} boxSize={5} />
											Add new property
										</Button>
									</Box>
								)}
							</Drawer.Body>
						</Drawer.Content>
					</Drawer.Positioner>
				</Portal>
			</Drawer.Root>

			<SavedCardPaymentConfirmationDialog
				isOpen={isConfirmationOpen}
				amountCents={pendingPayment?.expected_price_cents ?? 0}
				card={savedCard}
				isLoading={isCheckingOut}
				onCancel={cancelCheckout}
				onConfirm={confirmCheckout}
			/>

			<SavedCardPaymentConfirmationDialog
				title="Activate Pro Subscription?"
				isOpen={isSubscribeConfirmationOpen}
				amountCents={subscribePendingAmountCents}
				card={subscribeSavedCard}
				isLoading={isSubscribeCheckingOut}
				onCancel={cancelSubscribeCheckout}
				onConfirm={confirmSubscribeCheckout}
			/>
		</>
	);
};

export default PropertySelectorMobile;
